import { createContext, useContext, useEffect, useMemo, useState, useCallback, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "../supabaseClient";

type AuthState =
  | { status: "loading"; user: null; session: null; passwordRecovery: false }
  | { status: "signed_out"; user: null; session: null; passwordRecovery: false }
  | { status: "signed_in"; user: User; session: Session; passwordRecovery: boolean };

// Intersection, not `interface extends` — an interface can't extend a union,
// and doing so silently erased the discriminated `status`/`user` narrowing.
type AuthContextValue = AuthState & {
  clearPasswordRecovery: () => void;
};

const AuthContext = createContext<AuthContextValue>({
  status: "loading",
  user: null,
  session: null,
  passwordRecovery: false,
  clearPasswordRecovery: () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading", user: null, session: null, passwordRecovery: false });

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      const session = data.session;
      if (session?.user) setState({ status: "signed_in", user: session.user, session, passwordRecovery: false });
      else setState({ status: "signed_out", user: null, session: null, passwordRecovery: false });
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      if (session?.user) {
        setState({
          status: "signed_in",
          user: session.user,
          session,
          passwordRecovery: event === "PASSWORD_RECOVERY",
        });
      } else {
        setState({ status: "signed_out", user: null, session: null, passwordRecovery: false });
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const clearPasswordRecovery = useCallback(() => {
    setState((prev) => {
      if (prev.status === "signed_in") return { ...prev, passwordRecovery: false };
      return prev;
    });
  }, []);

  const value: AuthContextValue = useMemo(
    () => ({ ...state, clearPasswordRecovery }),
    [state, clearPasswordRecovery]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
